import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { getPricingCustomerList, getPricingDistrictItemList, updateExecutedPrice } from '../../queries/ScenarioForecast'
import PlusRoundIcon from "@rsuite/icons/PlusRound";
import MinusRoundIcon from "@rsuite/icons/MinusRound";
import { numberFormatter } from '../../utils/formatter';
import { Button, Pagination } from 'rsuite';
import { pageSize } from '../../constants/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Box } from '@mui/material';
import useFilter from '../../hooks/useFilter';

type Props = {
    filterValues: any,
    setLoader: React.Dispatch<React.SetStateAction<number>>;
    setOpportunity: React.Dispatch<React.SetStateAction<PricingMinMaxopportunity>>;
    opportunity: PricingMinMaxopportunity;
    setGraphRequestData: React.Dispatch<React.SetStateAction<PricingGraphRequest | null>>
}

const CustomerLevelColumns = ["Customer", "District", "Quantity (eaches/year)", "Revenue ($/year)", "Opportunity ($)", "Action"]

const DistrictLevelColumns = ["Customer", "District", "Item ID", "Quantity (eaches/year)", "Revenue ($/year)", "Opportunity ($)", "Action"]

const ItemLevelColumns = ["Customer", "District", "Item ID", "Item Desc", "Quantity (eaches/year)", "Revenue ($/year)", "Target Price ($)", "Current Price ($)", "Adjusted Price ($)", "Opportunity ($)", "Action"]
const columnStyleClassWithoutALignCenter = "table-cell-text text-black border-[0.1px] border-[#bebebe] p-[0.5vw]"

const columnStyleClass = columnStyleClassWithoutALignCenter + " alignCenter"
const PricingSummaryTable = forwardRef<PricingSearchRef, Props>(({ filterValues, setLoader, setOpportunity, opportunity, setGraphRequestData }, ref) => {
    const opportunitySaved = useRef(false)
    const [columns, setColumns] = useState<string[]>(CustomerLevelColumns);
    const [expandedCustomerRow, setExpandedCustomerRow] = useState<number | null>(null);
    const [expandedDistrictRow, setExpandedDistrictRow] = useState<number | null>(null);
    const [customerList, setCustomerList] = useState<PricingCustomerList[]>([]);
    const [divisionItemList, setDivisionItemList] = useState<PricingDivisionList>({});
    const [totalRow, setTotalRow] = useState<number>(0);
    const [totalOpportunity, setTotalOpportunity] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState(1);
    const { selectedFilter } = useFilter();
    useEffect(() => {
        if (selectedFilter == "electrical" && !filterValues?.projectFlag?.length) return
        if (selectedFilter == "plumbing" && !filterValues?.pc) return
        fnFetchCustomerLevelData();
        setColumns(CustomerLevelColumns);
        setExpandedCustomerRow(null)
        setExpandedDistrictRow(null)
        setGraphRequestData(null)
        setDivisionItemList({})
    }, [filterValues, currentPage]);


    const fnFetchCustomerLevelData = async () => {
        try {
            setLoader((prev) => prev + 1)
            const response = await getPricingCustomerList(filterValues, opportunity, currentPage, selectedFilter);
            const data = response.data.data
            setCustomerList(data.list);
            setTotalRow(data.pricingTotalRecords[0].count)
            if (!opportunitySaved.current) {
                setOpportunity({ max: data.maxOpp + "", min: data.minOpp + "" })
                opportunitySaved.current = true
            }
            setTotalOpportunity(data.pricingTotalRecords[0].opportunity)
            changedValues.current = new Map()
            return data.list
        }
        catch (err) {
            console.log("Error while fething Pricing Customer ", err)
        }
        finally {
            setLoader((prev) => prev - 1)
        }
    }
    useImperativeHandle(ref, () => ({
        executeSearch: async (isAfterUpdateAction?: boolean) => {
            const customerList = await fnFetchCustomerLevelData();
            if (isAfterUpdateAction && expandedCustomerRow != null && customerList) {
                await fnFetchDistrictAndItemLevelData(customerList[expandedCustomerRow].bill_customer_id);
            }
        }
    }))

    const fnFetchDistrictAndItemLevelData = async (customerId: string) => {


        try {
            setLoader(prev => prev + 1)
            const response = await getPricingDistrictItemList({ ...filterValues, bill_customer_id: [customerId] }, opportunity, selectedFilter);
            const data = response.data.data.list[0].divisionList;
            Object.entries(data).forEach(([key, value]) => {
                let divisionAgreed = false
                value.list.forEach((item) => {
                    item.copyAdjustedPrice = item.adjustedPrice
                    if (item?.isAgreed === 0) {
                        divisionAgreed = true
                    }
                });
                value.divisionAggreed = divisionAgreed
            })
            setDivisionItemList(data)
        }
        catch (err) {
            console.log("Error fetchimg District Item List for customer:", customerId, err)
        }
        finally {
            setLoader(prev => prev - 1)

        }
    }

    const toggleCustomerExpansion = (index: number, customerId: string) => {
        if (expandedCustomerRow !== index) {
            fnFetchDistrictAndItemLevelData(customerId)
            setExpandedCustomerRow(index)
            setExpandedDistrictRow(null)
            setColumns(DistrictLevelColumns)
        }
        else {
            setExpandedCustomerRow(null)
            setColumns(CustomerLevelColumns)
        }

    }

    const toggleDivisionExpansion = (index: number) => {
        if (expandedDistrictRow !== index) {
            setExpandedDistrictRow(index)
            setColumns(ItemLevelColumns)
        }
        else {
            setExpandedDistrictRow(null)
            setColumns(DistrictLevelColumns)
        }
    }

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    }

    const getKeyOfChangedValues = (customerIndex: number, division: string) => {
        return customerIndex + "_" + division
    }
    const changedValues = useRef(new Map<string, Set<number>>());
    const handleAdjustedValueChange = (division: string, indexOfItem: number, newAdjustedValue: number, customerIndex: number) => {
        const key = getKeyOfChangedValues(customerIndex, division)
        if (changedValues.current.has(key))
            changedValues.current.get(key)?.add(indexOfItem);
        else
            changedValues.current.set(key, new Set([indexOfItem]));
        const selecteddivision = divisionItemList[division];
        const item = selecteddivision.list[indexOfItem];
        const prevItemOpportunity = item.opportunity
        const newItemOpportunity = calcOpprtunity(+item.quantity, item.followingPrice, newAdjustedValue)
        setCustomerList((prev) => {
            prev[customerIndex].opportunity += (newItemOpportunity - prevItemOpportunity)
            return [...prev]
        })
        setDivisionItemList((prev) => {
            prev[division].total.opportunity += (newItemOpportunity - prevItemOpportunity)
            const selectedItem = prev[division].list[indexOfItem]
            selectedItem.opportunity = newItemOpportunity
            selectedItem.adjustedPrice = newAdjustedValue;
            return { ...prev }
        })
    }

    const calcOpprtunity = (quantity: number, followingPrice: number, adjustedPrice: number) => {
        return quantity * (adjustedPrice - followingPrice);
    }

    const handleAcceptReject = async (itemIndex: number, division: string, customerIndex: number, isAccept: boolean, level: "division" | "customer" | "item") => {
        try {
            setLoader((prev) => prev + 1)
            const isReject = isAccept ? 0 : 1;
            const payload = acceptPayloadCreation(itemIndex, division, customerIndex, level, isReject);
            let levelFlag: 2 | 1 | 0;
            switch (level) {
                case 'customer':
                    levelFlag = 2
                    break;
                case 'division':
                    levelFlag = 1
                    break;
                case 'item':
                    levelFlag = 0
                    break;
            }
            const response = await updateExecutedPrice(payload, levelFlag, isReject, selectedFilter, filterValues)
            changedValues.current = new Map()
            setGraphRequestData((prev) => {
                if (!prev) return prev;
                return { ...prev }
            })
            const customerList = await fnFetchCustomerLevelData();
            if (expandedCustomerRow != null && customerList) {
                await fnFetchDistrictAndItemLevelData(customerList[expandedCustomerRow].bill_customer_id);
            }

        }
        catch (err) {
            console.log(err)
        }
        finally {
            setLoader((prev) => prev - 1)

        }
    }

    const acceptPayloadCreation = (itemIndex: number, division: string, customerIndex: number, level: "division" | "customer" | "item", isReject: 0 | 1) => {
        const customer = customerList[customerIndex]
        const payload: PricingUpdateRejectRequest = { data: [] }
        switch (level) {
            case "item":
                const selecteddivisionForItem = divisionItemList[division];
                const selectedItem = selecteddivisionForItem.list[itemIndex];
                payload.data.push({
                    customerId: selectedItem.bill_customer_id,
                    division: division,
                    executedPrice: selectedItem.adjustedPrice,
                    isAgreed: selectedItem.isAgreed,
                    itemId: selectedItem.itemId,
                    opportunity: selectedItem.opportunity,
                    peerGroupName: selectedItem.peerGroupName,
                    peerPrice: selectedItem.Avg_peer_Price,
                    project_flag: selectedItem.project_flag,
                    isRecOverrideFlag: selectedItem?.Avg_peer_Price !== selectedItem?.adjustedPrice ? 1 : 0,
                })
                break;
            case "division":
                const selecteddivision = divisionItemList[division];
                payload.filter = { ...filterValues, bill_customer_id: [customer.bill_customer_id], division: [division] };
                if (selectedFilter == "electrical" && payload?.filter) {
                    payload.filter.maxOpp = opportunity.max
                    payload.filter.minOpp = opportunity.min
                }
                const key = getKeyOfChangedValues(customerIndex, division)
                const data = changedValues.current.get(key);

                if (!isReject)
                    data?.forEach((changedItemindex) => {
                        const value = selecteddivision.list[changedItemindex]
                        payload.data.push({
                            customerId: value.bill_customer_id,
                            division: division,
                            executedPrice: value.adjustedPrice,
                            isAgreed: value.isAgreed,
                            itemId: value.itemId,
                            opportunity: value.opportunity,
                            peerGroupName: value.peerGroupName,
                            peerPrice: value.Avg_peer_Price,
                            project_flag: value.project_flag,
                            isRecOverrideFlag: value?.Avg_peer_Price !== value?.adjustedPrice ? 1 : 0,
                        })
                    })
                break;
            case 'customer':
                payload.filter = { ...filterValues, bill_customer_id: [customer.bill_customer_id], maxOpp: opportunity.max, minOpp: opportunity.min };
                Object.entries(divisionItemList).map(([divisionString, divisionItems]) => {
                    const key = getKeyOfChangedValues(customerIndex, divisionString)
                    const data = changedValues.current.get(key);
                    const selecteddivision = divisionItemList[divisionString];
                    if (!isReject)
                        data?.forEach((changedItemindex) => {
                            const value = selecteddivision.list[changedItemindex]
                            payload.data.push({
                                customerId: value.bill_customer_id,
                                division: divisionString,
                                executedPrice: value.adjustedPrice,
                                isAgreed: value.isAgreed,
                                itemId: value.itemId,
                                opportunity: value.opportunity,
                                peerGroupName: value.peerGroupName,
                                peerPrice: value.Avg_peer_Price,
                                project_flag: value.project_flag,
                                isRecOverrideFlag: value?.Avg_peer_Price !== value?.adjustedPrice ? 1 : 0,
                            })
                        })
                })
                break
            default:
                break;
        }
        return payload;
    }
    return (
        <div className='h-full'>
            <div className="chart-title subHead">Customer Summary ({totalRow || 0} Customers, ${numberFormatter(totalOpportunity)} Opportunity)</div>
            <div className='border border-lightGrey bg-white h-full shadow-md'>
                {customerList?.length > 0 && <>
                    <Box className='relative overflow-auto'
                        sx={{ maxHeight: `calc(100% - 30px)`, height: "100%" }}
                    >
                        <table className=" fixed_header table-container table-stripped-alternate">
                            <tbody className="tbodyClass text-[0.75vw] border-separate">
                                <tr>
                                    {columns.map((d, index) => (
                                        <th
                                            key={d}
                                            className="table-header-text font-semibold alignCenter itemTableHeader bg-white text-black border border-lightGrey border-b-[2px] border-b-deloitteGreen "
                                        >
                                            {d}
                                        </th>
                                    ))}
                                </tr>
                                {customerList?.map(({ bill_customer_id, customerDesc, division, opportunity, quantity, revenue, isAgreed, Customer_Class }, index) => (
                                    <React.Fragment key={bill_customer_id + customerDesc + Customer_Class}>
                                        <tr  >
                                            <td className={columnStyleClassWithoutALignCenter + " whitespace-nowrap"}>
                                                {expandedCustomerRow === index ? (
                                                    <MinusRoundIcon
                                                        className='mr-2 cursor-pointer'
                                                        onClick={() =>
                                                            toggleCustomerExpansion(index, bill_customer_id)
                                                        }
                                                    />
                                                ) : (
                                                    <PlusRoundIcon
                                                        className={`mr-2 ${expandedCustomerRow ===
                                                            null ? "cursor-pointer opacity-100" : "cursor-not-allowed opacity-50"}`}
                                                        onClick={() =>
                                                            expandedCustomerRow ===
                                                            null &&
                                                            toggleCustomerExpansion(index, bill_customer_id)
                                                        }
                                                    />
                                                )}
                                                {customerDesc}
                                            </td>
                                            <td className={columnStyleClass}>
                                                Total: {division}
                                            </td>
                                            {columns.includes("Item ID") &&
                                                <td className={columnStyleClass}>

                                                </td>}
                                            {columns.includes("Item Desc") &&
                                                <td className={columnStyleClass}>

                                                </td>}
                                            {columns.includes("Quantity (eaches/year)") &&
                                                <td className={columnStyleClass}>
                                                    {numberFormatter(quantity)}
                                                </td>
                                            }
                                            {columns.includes("Revenue ($/year)") &&
                                                <td className={columnStyleClass}>
                                                    ${numberFormatter(revenue)}
                                                </td>
                                            }
                                            {columns.includes("Target Price ($)") &&
                                                <td className={columnStyleClass}>

                                                </td>}

                                            {columns.includes("Current Price ($)") &&
                                                <td className={columnStyleClass}>

                                                </td>}

                                            {columns.includes("Adjusted Price ($)") &&
                                                <td className={columnStyleClass}>
                                                </td>}

                                            {columns.includes("Opportunity ($)") &&
                                                <td className={columnStyleClass}>
                                                    ${numberFormatter(opportunity)}
                                                </td>
                                            }
                                            <td className={columnStyleClass + " alignCenter"} >
                                                <Button type="button"
                                                    className={`btn-text !text-white ${isAgreed == 0 ? "!bg-deloitteGreen" : "!bg-lightGrey"} `}
                                                    onClick={() => {
                                                        handleAcceptReject(0, "", index, !isAgreed, "customer");
                                                    }}> Accept All</Button>
                                            </td>
                                        </tr>
                                        <DivisionWiseRow onItemClick={setGraphRequestData} columns={columns} divisionItemList={divisionItemList} expandedCustomerRow={expandedCustomerRow} expandedDistrictRow={expandedDistrictRow} customerIndex={index} toggleDivisionExpansion={toggleDivisionExpansion} handleAdjustedValueChange={handleAdjustedValueChange} handleAcceptReject={handleAcceptReject} />

                                    </React.Fragment>
                                ))}

                            </tbody>
                        </table>
                    </Box>
                    <Box>
                        <Pagination
                            prev
                            next
                            first
                            last
                            ellipsis
                            boundaryLinks
                            maxButtons={2}
                            size="xs"
                            layout={["pager", "skip"]}
                            total={totalRow}
                            limit={pageSize}
                            activePage={currentPage}
                            onChangePage={handlePageChange}
                            className='text-[0.75vw text-black justify-center'
                        />
                    </Box>
                </>}
            </div>
        </div >
    )
})


type DivisionWiseRowProps = {
    onItemClick: React.Dispatch<React.SetStateAction<PricingGraphRequest | null>>,
    expandedCustomerRow: number | null,
    divisionItemList: PricingDivisionList,
    expandedDistrictRow: number | null, customerIndex: number,
    toggleDivisionExpansion: (index: number) => void, columns: string[],
    handleAdjustedValueChange: (division: string, indexOfItem: number, newAdjustedValue: number, customerIndex: number) => void,
    handleAcceptReject: (itemIndex: number, division: string, customerIndex: number, isAccept: boolean, level: "division" | "customer" | "item") => void
}

const DivisionWiseRow = ({ expandedCustomerRow, divisionItemList, expandedDistrictRow, customerIndex, toggleDivisionExpansion, columns, onItemClick, handleAdjustedValueChange, handleAcceptReject }: DivisionWiseRowProps) => {
    if (expandedCustomerRow !== customerIndex || !divisionItemList) {
        return null
    }
    return <>
        {Object.entries(divisionItemList).map(([key, value], divisionIndex) => (
            <React.Fragment key={key}>
                <tr>
                    <td className={columnStyleClass}>
                    </td>
                    <td className={columnStyleClass + " whitespace-nowrap"}>

                        {expandedDistrictRow === divisionIndex ? (
                            <MinusRoundIcon
                                className='mr-2 cursor-pointer'
                                onClick={() =>
                                    toggleDivisionExpansion(divisionIndex)
                                }
                            />
                        ) : (
                            <PlusRoundIcon
                                className={`mr-2 ${expandedDistrictRow ===
                                    null ? "cursor-pointer opacity-100" : "cursor-not-allowed opacity-50"}`}
                                onClick={() =>
                                    expandedDistrictRow ===
                                    null &&
                                    toggleDivisionExpansion(divisionIndex)
                                }
                            />
                        )}{key}</td>
                    <td className={columnStyleClass}>
                        total: {value.list.length}</td>
                    {columns.includes("Item Desc") &&
                        <td className={columnStyleClass}>

                        </td>}
                    <td className={columnStyleClass}>
                        {numberFormatter(value.total.quantity)}</td>
                    <td className={columnStyleClass}>
                        ${numberFormatter(value.total.revenue)}</td>
                    {columns.includes("Target Price ($)") &&
                        <td className={columnStyleClass}>

                        </td>}

                    {columns.includes("Current Price ($)") &&
                        <td className={columnStyleClass}>

                        </td>}
                    {columns.includes("Adjusted Price ($)") &&
                        <td className={columnStyleClass}>
                        </td>}

                    <td className={columnStyleClass}>
                        ${numberFormatter(value.total.opportunity)}</td>
                    <td className={columnStyleClass + ' alignCenter'}>

                        <FontAwesomeIcon
                            icon={
                                faCheck
                            }
                            className={`!mr-[2vh] !ml-[1.8vh] !h-[2.5vh] !p-[1%] ${!value.divisionAggreed ? "rejectIcon" : "acceptIcon"} cursor-pointer`}
                            onClick={(e) => { e.stopPropagation(); handleAcceptReject(0, key, customerIndex, value.divisionAggreed, 'division') }}
                            aria-hidden="true"
                        /></td>
                </tr>
                <ItemWiseRow expandedDistrictRow={expandedDistrictRow} divisionIndex={divisionIndex} customerIndex={customerIndex} value={value} onItemClick={onItemClick} handleAdjustedValueChange={handleAdjustedValueChange} handleAcceptReject={handleAcceptReject} />
            </React.Fragment>
        ))}
    </>
}


type ItemWiseRowProps = {
    expandedDistrictRow: number | null,
    divisionIndex: number,
    customerIndex: number,
    value: PricingDivision,
    onItemClick: React.Dispatch<React.SetStateAction<PricingGraphRequest | null>>,
    handleAdjustedValueChange: (division: string, indexOfItem: number, newAdjustedValue: number, customerIndex: number) => void;
    handleAcceptReject: (itemIndex: number, division: string, customerIndex: number, isAccept: boolean, level: "division" | "customer" | "item") => void
}

const ItemWiseRow = ({ expandedDistrictRow, divisionIndex, customerIndex, value, onItemClick, handleAdjustedValueChange, handleAcceptReject }: ItemWiseRowProps) => {
    if (expandedDistrictRow !== divisionIndex || value?.list.length < 0)
        return null
    return <>
        {value.list.map(({ itemId, Description, quantity, revenue, adjustedPrice, followingPrice, Avg_peer_Price, opportunity, bill_customer_id, peerGroupName, project_flag, division, isAgreed }, itemIndex) => (
            <tr key={itemId + "_" + quantity} className='cursor-pointer ' onClick={() => {
                onItemClick({ customer: bill_customer_id, itemId, peerGroupName, division, project_flag })
            }}>
                <td className={columnStyleClass}>
                </td>
                <td className={columnStyleClass}>
                </td>
                <td className={columnStyleClass}>
                    {itemId}</td>
                <td className={columnStyleClass}>
                    {Description}</td>
                <td className={columnStyleClass}>
                    {numberFormatter(+quantity || 0)}</td>
                <td className={columnStyleClass}>
                    ${numberFormatter(revenue)}</td>
                <td className={columnStyleClass}>
                    ${numberFormatter(Avg_peer_Price)}</td>
                <td className={columnStyleClass}>
                    ${numberFormatter(followingPrice)}</td>
                <td className={columnStyleClass} onClick={(e) => e.stopPropagation()}>
                    <input type="number" className="border border-[#BEBEBE] p-1 rounded-sm bg-[#F1F1F1]" value={adjustedPrice} onChange={(e) => {
                        handleAdjustedValueChange(division, itemIndex, e.target.value ? +e.target.value : 0, customerIndex)
                    }} />
                </td>
                <td className={columnStyleClass}>
                    ${numberFormatter(opportunity)}</td>
                <td className={columnStyleClass + ' alignCenter'} onClick={(e) => e.stopPropagation()}>
                    <FontAwesomeIcon
                        icon={
                            faCheck
                        }
                        className={`!mr-[2vh] !ml-[1.8vh] !h-[2.5vh] !p-[1%] ${isAgreed ? "rejectIcon" : "acceptIcon"} cursor-pointer`}
                        onClick={(e) => { e.stopPropagation(); handleAcceptReject(itemIndex, division, customerIndex, !isAgreed, 'item') }}
                        aria-hidden="true"
                    />
                </td>
            </tr>))}</>
}

export default PricingSummaryTable